import React from 'react';
import { Autocomplete, TextField, Checkbox } from '@mui/material';
import { CANtrackCase } from '../../types/comment';
import { CaseFilters } from '../../hooks/useCases';
import {
	CommentFormState,
	CommentFormStateErrors,
} from '../../hooks/useCommentEditorForm';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

const icon = <PushPinOutlinedIcon fontSize="small" />;
const checkedIcon = <PushPinIcon fontSize="small" color="primary" />;

interface CaseSearchAutocompleteProps {
	formState: CommentFormState;
	loadingCases: boolean;
	cases: CANtrackCase[];
	totalCases: number;
	pinnedCases: CANtrackCase[];
	errors: CommentFormStateErrors;
	handleSearchChange: (event: any) => void;
	setAppliedFilter: React.Dispatch<React.SetStateAction<CaseFilters>>;
	updatePinnedCases: (option: CANtrackCase) => void;
	handleChange: <T extends keyof CommentFormState>(
		field: T,
		value: CommentFormState[T],
		validate?: boolean,
	) => void;
	disabled?: boolean;
}

const CaseSearchAutocomplete = ({
	formState,
	loadingCases,
	cases,
	totalCases,
	pinnedCases,
	errors,
	handleChange,
	handleSearchChange,
	setAppliedFilter,
	updatePinnedCases,
	disabled,
}: CaseSearchAutocompleteProps) => {
	return (
		<Autocomplete
			disabled={disabled}
			value={formState.selectedCase}
			options={[...pinnedCases, ...cases]}
			loading={loadingCases}
			loadingText="Loading cases"
			onChange={(e, newValue) => {
				handleChange('selectedCase', newValue);
				// Reset search terms
				setAppliedFilter((prev) => {
					return {
						...prev,
						Page: 1,
						Take: prev.Take,
						SearchKeyword: '',
					};
				});
			}}
			groupBy={(option) =>
				pinnedCases.some((pinnedCase) => pinnedCase.id === option.id)
					? 'Pinned Cases'
					: 'Cases'
			}
			getOptionLabel={(option) => option.title || 'Unnamed Case'}
			renderOption={(props, option) => {
				const {  ...optionProps } = props;
				return (
					<li  {...optionProps}>
						<span style={{ flexGrow: 1 }}>{option.title}</span>
						<Checkbox
							icon={icon}
							checkedIcon={checkedIcon}
							style={{ marginRight: 8 }}
							checked={pinnedCases.some(
								(i) => i.id === option.id,
							)}
							onClick={(e) => {
								e.stopPropagation();
								updatePinnedCases(option);
							}}
						/>
					</li>
				);
			}}
			filterOptions={(options, params) => {
				const filtered = options.filter((option) => {
					if (
						option.id.toString() === params.inputValue.toLowerCase()
					) {
						return true;
					}
					if (!params.inputValue) {
						return true;
					}
					return (
						option.title
							.toLowerCase()
							.indexOf(params.inputValue.toLowerCase()) !== -1
					);
				});

				// Sort pinned cases to the top
				const pinned = pinnedCases.filter((pinnedCase) =>
					filtered.some((option) => option.id === pinnedCase.id),
				);
				const unpinned = filtered.filter(
					(option) =>
						!pinnedCases.some(
							(pinnedCase) => pinnedCase.id === option.id,
						),
				);

				return [...pinned, ...unpinned];
			}}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			fullWidth
			onBlur={() => {
				setAppliedFilter((prev) => {
					return {
						...prev,
						SearchKeyword: '',
					};
				});
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={
						formState.selectedCase
							? `Selected Case`
							: loadingCases
							? 'Loading Cases...'
							: `Cases (${
									totalCases < 99 ? totalCases : '99+'
							  } Results)`
					}
					variant="outlined"
					onChange={handleSearchChange}
					error={!!errors.selectedCase}
					helperText={errors.selectedCase}
				/>
			)}
		/>
	);
};

export default CaseSearchAutocomplete;
